import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Link,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  createCatalogCheckout,
  createReactivateCheckout,
  applyDiscountCode,
  cancelSubscription,
  getPortalUrl,
  reactivateSubscription,
  removeCatalog,
  removeDiscountCode,
  verifyCatalogCheckout,
  type Catalog,
  type SubscriptionState,
} from '../../utils/subscriptionApi';
import { featureFlags } from '../../utils/featureFlags';
import type { BillingPeriod } from '../../utils/paymentTiers';
import { useSubscription } from './hooks/useSubscription';
import { LabelCaps } from './LabelCaps';
import { PlanHeader } from './PlanHeader';
import { CatalogList } from './CatalogList';
import { DiscountRow } from './DiscountRow';
import { SubscriptionFooter } from './SubscriptionFooter';
import { AddCatalogDialog } from './dialogs/AddCatalogDialog';
import { CancelSubscriptionDialog } from './dialogs/CancelSubscriptionDialog';
import { SwitchBillingPeriodDialog } from './dialogs/SwitchBillingPeriodDialog';

const SUPPORT_EMAIL = 'support@artlegacyinstitute.org';

const NoActivePlanState: React.FC<{ message?: string | null }> = ({ message }) => (
  <Box sx={{ p: 3 }} data-testid="subscription-page-empty">
    <Card variant="outlined" sx={{ p: 3 }}>
      <LabelCaps>Current Plan</LabelCaps>
      <Typography variant="h4" sx={{ mt: 0.5, mb: 1 }}>
        No active plan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We couldn&apos;t find an active subscription on your account.
      </Typography>
      {message && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      <Typography variant="body2">
        <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
          Contact Support
        </Link>{' '}
        to restore your subscription.
      </Typography>
    </Card>
  </Box>
);

type DialogId = 'add' | 'cancel' | 'switch' | null;

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

/** Pick the primary catalog from state, defensively. */
function pickPrimary(state: SubscriptionState): Catalog | null {
  return state.catalogs.find((c) => c.isPrimary) ?? state.catalogs[0] ?? null;
}

export const SubscriptionPage: React.FC = () => {
  const { state, loading, error, setState, refetch } = useSubscription();
  const [dialog, setDialog] = useState<DialogId>(null);
  const [switchTarget, setSwitchTarget] = useState<BillingPeriod>('yearly');
  const [switchCatalogAliId, setSwitchCatalogAliId] = useState<string | null>(null);
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });
  const [managingBilling, setManagingBilling] = useState(false);

  const notify = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnack({ open: true, message, severity });
  }, []);

  const apply = useCallback(
    async <T,>(work: () => Promise<T>, successMessage: string) => {
      try {
        const next = await work();
        if (next && typeof next === 'object' && 'tier' in (next as Record<string, unknown>)) {
          setState(next as unknown as SubscriptionState);
        } else {
          await refetch();
        }
        notify(successMessage, 'success');
      } catch (err) {
        notify((err as Error).message || 'Something went wrong.', 'error');
        throw err;
      }
    },
    [setState, refetch, notify]
  );

  // Detect return-from-Checkout (?catalog_success=true&session_id=…) and
  // verify the session. The user lands here after Stripe Checkout for an
  // add-catalog session redirects back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('catalog_success');
    const reactivated = params.get('catalog_reactivated');
    const sessionId = params.get('session_id');
    const cancelled = params.get('catalog_cancelled');

    if (success === 'true' && sessionId) {
      // Clear the params first so a manual refresh doesn't re-verify.
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      apply(() => verifyCatalogCheckout(sessionId), 'Catalog added.').catch(() => {
        // notify already fired inside apply.
      });
    } else if (reactivated === 'true' && sessionId) {
      // GH#1254: same idempotent verify endpoint — the server branches on
      // the session's purpose metadata and updates the existing row.
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      apply(() => verifyCatalogCheckout(sessionId), 'Catalog reactivated.').catch(() => {
        // notify already fired inside apply.
      });
    } else if (cancelled === 'true') {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      notify('Checkout cancelled.', 'error');
    }
    // Intentionally one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primary = state ? pickPrimary(state) : null;

  const handleSwitchCycle = (catalogAliId: string, target: BillingPeriod) => {
    setSwitchCatalogAliId(catalogAliId);
    setSwitchTarget(target);
    setDialog('switch');
  };

  const handleAddCatalog = async (input: Parameters<typeof createCatalogCheckout>[0]) => {
    try {
      const result = await createCatalogCheckout(input);
      // Redirect to Stripe Checkout. The verify-on-return effect above
      // picks up the success_url query params and finalizes the row.
      window.location.assign(result.url);
    } catch (err) {
      notify((err as Error).message || 'Failed to start checkout.', 'error');
      throw err;
    }
  };

  const handleRemoveCatalog = async (catalogAliId: string) => {
    await apply(() => removeCatalog(catalogAliId), 'Catalog removed.');
  };

  const handleApplyDiscount = async (code: string) => {
    if (!primary) return;
    await apply(() => applyDiscountCode(primary.catalogAliId, code), 'Discount code applied.');
  };

  const handleRemoveDiscount = async () => {
    if (!primary) return;
    await apply(() => removeDiscountCode(primary.catalogAliId), 'Discount code removed.');
  };

  const handleCancel = async (reason?: string, keepPublicVisible?: boolean) => {
    if (!primary) return;
    await apply(
      () => cancelSubscription(primary.catalogAliId, reason, keepPublicVisible),
      'Cancellation scheduled.'
    );
  };

  const handleReactivate = async () => {
    if (!primary) return;
    await apply(() => reactivateSubscription(primary.catalogAliId), 'Subscription reactivated.');
  };

  // GH#1254: reactivate an EXPIRED catalog — a brand-new Stripe Checkout
  // (the old subscription is gone). Reuses the row's displayed cycle; the
  // user can switch cycles in-app afterwards like any live catalog.
  const handleReactivateExpired = async (catalogAliId: string) => {
    const target = state?.catalogs.find((c) => c.catalogAliId === catalogAliId);
    try {
      const result = await createReactivateCheckout(catalogAliId, target?.billingPeriod ?? 'monthly');
      window.location.assign(result.url);
    } catch (err) {
      notify((err as Error).message || 'Failed to start reactivation checkout.', 'error');
    }
  };

  const expiryLifecycleEnabled = featureFlags.isEnabled('enableExpiryLifecycle');

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const url = await getPortalUrl();
      window.open(url, '_blank');
    } catch (err) {
      notify((err as Error).message || 'Unable to open billing portal.', 'error');
    } finally {
      setManagingBilling(false);
    }
  };

  if (loading && !state) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return <NoActivePlanState message={error} />;
  }

  if (!state || !primary) return <NoActivePlanState />;

  const switchCatalog =
    state.catalogs.find((c) => c.catalogAliId === switchCatalogAliId) ?? primary;
  const inactiveCycle: BillingPeriod = switchCatalog.billingPeriod === 'yearly' ? 'monthly' : 'yearly';

  return (
    <Box sx={{ p: 3 }} data-testid="subscription-page">
      {primary.cancelAtPeriodEnd && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleReactivate}>
              Reactivate
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Primary catalog is set to cancel at the end of the current period. Reactivate any time before then.
        </Alert>
      )}

      <Card variant="outlined" sx={{ overflow: 'hidden' }}>
        <PlanHeader state={state} primary={primary} />
        <Divider />
        <CatalogList
          catalogs={state.catalogs}
          onAdd={() => setDialog('add')}
          onRemove={handleRemoveCatalog}
          onSwitchCycle={handleSwitchCycle}
          onReactivate={expiryLifecycleEnabled ? handleReactivateExpired : undefined}
        />
        <Divider />
        <DiscountRow
          discount={primary.discountCode}
          onApply={handleApplyDiscount}
          onRemove={handleRemoveDiscount}
        />
        <Divider />
        <SubscriptionFooter
          onManageBilling={handleManageBilling}
          onCancel={() => setDialog('cancel')}
          managingBilling={managingBilling}
        />
      </Card>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
      >
        Payment method and invoice history are managed through our secure billing provider.
      </Typography>

      <AddCatalogDialog
        open={dialog === 'add'}
        onClose={() => setDialog(null)}
        onSubmit={handleAddCatalog}
        defaultBillingPeriod={primary.billingPeriod}
      />
      <CancelSubscriptionDialog
        open={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        onConfirm={handleCancel}
        onSwitchInstead={() => {
          setSwitchCatalogAliId(primary.catalogAliId);
          setSwitchTarget('yearly');
          setDialog('switch');
        }}
        renewDate={primary.renewDate}
        currentBillingPeriod={primary.billingPeriod}
      />
      <SwitchBillingPeriodDialog
        open={dialog === 'switch'}
        onClose={() => setDialog(null)}
        onConfirmed={() => {
          refetch();
          notify('Billing period updated.', 'success');
        }}
        catalogAliId={switchCatalog.catalogAliId}
        from={switchCatalog.billingPeriod}
        to={switchTarget !== switchCatalog.billingPeriod ? switchTarget : inactiveCycle}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack({ ...snack, open: false })}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
